import { supabase } from './supabase';
import { PlayerState } from '../components/simulator/PlayerCircle';

export interface Quiz {
  id: string;
  title: string;
  description?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export type QuestionType = 'free_text' | 'multiple_choice';

export interface QuestionOption {
  id: string;
  text: string;
}

export interface QuizQuestion {
  id: string;
  quiz_id: string;
  question_text: string;
  accepted_answer: string;
  question_type: QuestionType;
  options?: QuestionOption[];
  order_num: number;
  simulator_state?: PlayerState[];
  created_at: string;
}

export interface QuizLink {
  id: string;
  quiz_id: string;
  link_token: string;
  participant_name: string;
  expires_at: string;
  completed_at?: string;
  time_taken_minutes?: number;
  created_at: string;
}

export interface QuizResponse {
  id: string;
  quiz_link_id: string;
  question_id: string;
  response_text?: string;
  is_correct?: boolean;
  graded_at?: string;
  created_at: string;
  updated_at: string;
}

// Quiz CRUD operations
export const getAllQuizzes = async (): Promise<Quiz[]> => {
  const { data, error } = await supabase
    .from('quizzes' as any)
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as unknown as Quiz[]) || [];
};

export const getQuizById = async (quizId: string): Promise<Quiz | null> => {
  const { data, error } = await supabase
    .from('quizzes' as any)
    .select('*')
    .eq('id', quizId)
    .single();

  if (error) throw error;
  return data as unknown as Quiz | null;
};

export const createQuiz = async (title: string, description?: string): Promise<Quiz> => {
  const { data: userData } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('quizzes' as any)
    .insert({
      title,
      description,
      created_by: userData?.user?.id
    } as any)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as Quiz;
};

export const updateQuiz = async (id: string, updates: Partial<Quiz>): Promise<Quiz> => {
  const { data, error } = await supabase
    .from('quizzes' as any)
    .update(updates as any)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as Quiz;
};

export const deleteQuiz = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('quizzes' as any)
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// Quiz Questions
export const getQuizQuestions = async (quizId: string): Promise<QuizQuestion[]> => {
  const { data, error } = await supabase
    .from('quiz_questions' as any)
    .select('*')
    .eq('quiz_id', quizId)
    .order('order_num', { ascending: true });

  if (error) throw error;
  return (data as unknown as QuizQuestion[]) || [];
};

export const createQuestion = async (
  quizId: string,
  questionText: string,
  acceptedAnswer: string,
  orderNum: number,
  questionType: QuestionType = 'free_text',
  options?: QuestionOption[],
  simulatorState?: PlayerState[]
): Promise<QuizQuestion> => {
  const { data, error} = await supabase
    .from('quiz_questions' as any)
    .insert({
      quiz_id: quizId,
      question_text: questionText,
      accepted_answer: acceptedAnswer,
      question_type: questionType,
      options: options,
      order_num: orderNum,
      simulator_state: simulatorState
    } as any)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as QuizQuestion;
};

export const updateQuestion = async (id: string, updates: Partial<QuizQuestion>): Promise<QuizQuestion> => {
  const { data, error } = await supabase
    .from('quiz_questions' as any)
    .update(updates as any)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as QuizQuestion;
};

export const deleteQuestion = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('quiz_questions' as any)
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// Quiz Links
export const generateQuizLink = async (
  quizId: string,
  participantName: string,
  expiresAt: Date
): Promise<QuizLink> => {
  const token = crypto.randomUUID();

  const { data, error } = await supabase
    .from('quiz_links' as any)
    .insert({
      quiz_id: quizId,
      link_token: token,
      participant_name: participantName,
      expires_at: expiresAt.toISOString()
    } as any)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as QuizLink;
};

export const getQuizLinkByToken = async (token: string): Promise<QuizLink | null> => {
  const { data, error } = await supabase
    .from('quiz_links' as any)
    .select('*')
    .eq('link_token', token)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }
  return data as unknown as QuizLink | null;
};

export const getQuizLinksByQuizId = async (quizId: string): Promise<QuizLink[]> => {
  const { data, error } = await supabase
    .from('quiz_links' as any)
    .select('*')
    .eq('quiz_id', quizId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as unknown as QuizLink[]) || [];
};

export const submitQuiz = async (quizLinkId: string, timeTakenMinutes: number): Promise<void> => {
  const { error } = await supabase
    .from('quiz_links' as any)
    .update({
      completed_at: new Date().toISOString(),
      time_taken_minutes: timeTakenMinutes
    } as any)
    .eq('id', quizLinkId);

  if (error) throw error;
};

// Quiz Responses
export const upsertQuizResponse = async (
  quizLinkId: string,
  questionId: string,
  responseText: string
): Promise<QuizResponse> => {
  const { data, error } = await supabase
    .from('quiz_responses' as any)
    .upsert({
      quiz_link_id: quizLinkId,
      question_id: questionId,
      response_text: responseText
    } as any, {
      onConflict: 'quiz_link_id,question_id'
    })
    .select()
    .single();

  if (error) throw error;
  return data as unknown as QuizResponse;
};

export const getQuizResponses = async (quizLinkId: string): Promise<QuizResponse[]> => {
  const { data, error } = await supabase
    .from('quiz_responses' as any)
    .select('*')
    .eq('quiz_link_id', quizLinkId);

  if (error) throw error;
  return (data as unknown as QuizResponse[]) || [];
};

export const gradeResponse = async (responseId: string, isCorrect: boolean): Promise<QuizResponse> => {
  const { data, error} = await supabase
    .from('quiz_responses' as any)
    .update({
      is_correct: isCorrect,
      graded_at: new Date().toISOString()
    } as any)
    .eq('id', responseId)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as QuizResponse;
};

export const getQuizByLinkToken = async (token: string): Promise<{ quiz: Quiz; quizLink: QuizLink } | null> => {
  const quizLink = await getQuizLinkByToken(token);
  if (!quizLink) return null;

  const quiz = await getQuizById(quizLink.quiz_id);
  if (!quiz) return null;

  return { quiz, quizLink };
};
